// Génère une image "carte de collection" (format story 1080×1920) prête à
// partager sur Instagram/Snap, dessinée entièrement en canvas côté client.

const W = 1080, H = 1920;

const ensureFonts = async () => {
  try {
    await Promise.all([
      document.fonts.load("700 120px Bangers"),
      document.fonts.load("800 60px Inter"),
      document.fonts.load("600 40px Inter"),
    ]);
    await document.fonts.ready;
  } catch {}
};

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const wrapText = (ctx, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let cur = '';
  words.forEach(w => {
    const test = cur ? cur + ' ' + w : w;
    if (ctx.measureText(test).width > maxWidth && cur) { lines.push(cur); cur = w; }
    else cur = test;
  });
  if (cur) lines.push(cur);
  return lines;
};

const drawBookSpines = (ctx, cx, y, scale = 1) => {
  const bars = [
    { w: 340, h: 46, color: '#F04E1A', dark: '#C83E12' },
    { w: 300, h: 46, color: '#F5C842', dark: '#D4A800' },
    { w: 340, h: 46, color: '#3B6FBF', dark: '#2A509A' },
    { w: 240, h: 46, color: '#5A9E7A', dark: '#3D7A5A' },
  ];
  let yy = y;
  bars.forEach(b => {
    const w = b.w * scale, h = b.h * scale;
    ctx.fillStyle = b.color;
    roundRect(ctx, cx - w / 2, yy, w, h, 8 * scale); ctx.fill();
    ctx.fillStyle = b.dark;
    roundRect(ctx, cx - w / 2, yy, 10 * scale, h, 4 * scale); ctx.fill();
    yy += h + 14 * scale;
  });
  return yy;
};

const drawHeader = (ctx) => {
  ctx.textAlign = 'center';
  ctx.fillStyle = '#F04E1A';
  ctx.font = "700 44px Bangers";
  ctx.fillText('BULLÉTHÈQUE', W / 2, 130);
};

const drawFooter = (ctx, label) => {
  ctx.textAlign = 'center';
  ctx.fillStyle = '#B5A99A';
  ctx.font = "600 30px Inter";
  ctx.fillText(label, W / 2, H - 70);
};

const background = (ctx) => {
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, '#FDFAF6');
  grad.addColorStop(1, '#FFF3EE');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);
};

const progressRing = (ctx, cx, cy, r, pct, color) => {
  ctx.lineWidth = 26;
  ctx.strokeStyle = '#EAE4DC';
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.arc(cx, cy, r, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.min(pct, 1));
  ctx.stroke();
};

// ─── Carte "Série" ────────────────────────────────────────────────────────
const drawSerieCard = (ctx, { serie, owned, total, complete, horsSerie }) => {
  drawHeader(ctx);

  ctx.textAlign = 'center';
  ctx.fillStyle = '#1C1612';
  ctx.font = "700 92px Bangers";
  const lines = wrapText(ctx, serie.toUpperCase(), 900);
  let ty = 320;
  lines.slice(0, 2).forEach(l => { ctx.fillText(l, W / 2, ty); ty += 100; });

  const cy = 780;
  const pct = total ? owned / total : 0;
  progressRing(ctx, W / 2, cy, 260, pct, complete ? '#16A34A' : '#F04E1A');
  ctx.font = "700 150px Bangers";
  ctx.fillStyle = '#1C1612';
  ctx.fillText(String(owned), W / 2, cy + 45);
  ctx.font = "600 42px Inter";
  ctx.fillStyle = '#8A7E72';
  ctx.fillText(total ? `sur ${total} tomes` : 'tomes possédés', W / 2, cy + 105);
  if (horsSerie > 0) {
    ctx.font = "600 34px Inter";
    ctx.fillStyle = '#B5A99A';
    ctx.fillText(`+ ${horsSerie} hors-série${horsSerie>1?'s':''}`, W / 2, cy + 150);
  }

  const baseY = cy + (horsSerie>0 ? 300 : 340);
  if (complete) {
    const bw = 480, bh = 90, by = baseY;
    ctx.fillStyle = '#16A34A';
    roundRect(ctx, W / 2 - bw / 2, by, bw, bh, 45); ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = "700 42px Inter";
    ctx.fillText('✓ COLLECTION COMPLÈTE', W / 2, by + 58);
    drawBookSpines(ctx, W / 2, by + 170, 1);
  } else {
    drawBookSpines(ctx, W / 2, baseY, 1);
  }

  drawFooter(ctx, 'Ma collection de BD');
};

// ─── Carte "Toute la collection" ───────────────────────────────────────────
const drawCollectionCard = (ctx, { total, series, complete }) => {
  drawHeader(ctx);

  ctx.textAlign = 'center';
  ctx.fillStyle = '#1C1612';
  ctx.font = "700 78px Bangers";
  ctx.fillText('MA COLLECTION', W / 2, 300);

  ctx.font = "700 320px Bangers";
  ctx.fillStyle = '#F04E1A';
  ctx.fillText(String(total), W / 2, 680);
  ctx.font = "600 46px Inter";
  ctx.fillStyle = '#8A7E72';
  ctx.fillText(`album${total>1?'s':''} de BD`, W / 2, 750);

  // Stat row
  const statY = 950, gap = 320;
  [{ v: series, l: 'séries' }, { v: complete, l: 'complètes' }].forEach((s, i) => {
    const x = W / 2 + (i === 0 ? -gap / 2 : gap / 2);
    ctx.font = "700 90px Bangers"; ctx.fillStyle = '#3B6FBF';
    ctx.fillText(String(s.v), x, statY);
    ctx.font = "600 34px Inter"; ctx.fillStyle = '#8A7E72';
    ctx.fillText(s.l, x, statY + 50);
  });
  ctx.strokeStyle = '#EAE4DC'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(W/2, statY-70); ctx.lineTo(W/2, statY+20); ctx.stroke();

  drawBookSpines(ctx, W / 2, 1140, 1.15);
  drawFooter(ctx, 'Ma bibliothèque de BD & mangas');
};

// ─── Carte "Manquants" ──────────────────────────────────────────────────────
const drawMissingCard = (ctx, { serie, missing }) => {
  drawHeader(ctx);

  ctx.textAlign = 'center';
  ctx.fillStyle = '#1C1612';
  ctx.font = "700 76px Bangers";
  const lines = wrapText(ctx, (serie || 'Toutes séries').toUpperCase(), 900);
  let ty = 300;
  lines.slice(0, 2).forEach(l => { ctx.fillText(l, W / 2, ty); ty += 90; });

  ctx.font = "700 260px Bangers";
  ctx.fillStyle = '#F04E1A';
  ctx.fillText(String(missing), W / 2, 760);
  ctx.font = "600 46px Inter";
  ctx.fillStyle = '#8A7E72';
  ctx.fillText(`tome${missing>1?'s':''} manquant${missing>1?'s':''}`, W / 2, 830);

  const bw = 440, bh = 90, by = 950;
  ctx.fillStyle = '#F5C842';
  roundRect(ctx, W/2-bw/2, by, bw, bh, 45); ctx.fill();
  ctx.fillStyle = '#1C1612';
  ctx.font = "700 38px Inter";
  ctx.fillText('AIDE-MOI À LA COMPLÉTER !', W/2, by+58);

  drawBookSpines(ctx, W / 2, by + 190, 1);
  drawFooter(ctx, 'Ma collection de BD');
};

/**
 * Génère l'image et retourne { canvas, toBlob(type,quality) }.
 * kind: 'serie' | 'collection' | 'missing'
 */
export async function renderShareCard(kind, data) {
  await ensureFonts();
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  background(ctx);
  if (kind === 'serie') drawSerieCard(ctx, data);
  else if (kind === 'collection') drawCollectionCard(ctx, data);
  else drawMissingCard(ctx, data);
  return canvas;
}

export const canvasToBlob = (canvas) =>
  new Promise(resolve => canvas.toBlob(resolve, 'image/png', 0.95));
